// ============================================================================
// JARVISTRAVEL - PAYMENTS API
// Version: 3.0.0 — Stripe (JAR-657)
// Payment processing and subscription management API calls
// Supports subscription plans (Explore) and one-time purchases (Trip Pass)
// The backend talks to Stripe directly. Clerk Billing sat here briefly
// (JAR-198/JAR-400) and was removed: it cannot express a repeatable one-time
// à la carte purchase, which is exactly what a Trip Pass is. Clerk is auth
// only. The stripe_* field names are the real contract now, not compat.
// ============================================================================

import { api, isDemoSession } from './client';
import { getBackendStatus } from './backendStatus';

// Representative demo tiers — the real plans/prices/names come from the
// backend (/payments/plans), which resolves them from Stripe; placeholders so
// the paywall renders in the demo. Nothing here is the source of truth.
const DEMO_PLANS: SubscriptionPlan[] = [
  {
    id: 'trip_pass', name: 'Trip Pass', stripe_price_id: 'price_demo_pass',
    description: 'One trip, planned and lived. Your journal is yours to keep.',
    price_cents: 2495, currency: 'usd', interval_type: 'one_time', product_type: 'one_time',
    features: { trips: 1 }, is_active: true, sort_order: 1,
  },
  {
    id: 'explore_annual', name: 'Explore', stripe_price_id: 'price_demo_annual',
    description: 'Unlimited trips, all year: planning, maps, budget, and Jarvis.',
    price_cents: 9900, currency: 'usd', interval_type: 'year', product_type: 'subscription',
    features: { trips: 'unlimited' }, is_active: true, sort_order: 2,
  },
];

/** What the current user is entitled to. Journal access is always preserved —
 *  even a Trip Pass user, after their pass is consumed, keeps their journals. */
export interface Entitlement {
  tier: 'trip_pass' | 'subscriber' | 'free';
  /** Start a new trip without paying (a subscriber, or holding an unused pass). */
  canCreateTrip: boolean;
  journalAccess: boolean;
  /**
   * Unspent Trip Passes. Already counted to derive the two flags above, and
   * previously discarded — which made a SECOND pass invisible: `tier` stays
   * `trip_pass` and `canCreateTrip` stays true, so nothing in this shape
   * changed when a repeat purchase landed. The checkout return screen had no
   * way to tell a completed purchase from one that has not arrived (JAR-1698).
   */
  passesAvailable: number;
}

/**
 * Did the purchase land? Compares the entitlement now against the one held
 * before checkout started.
 *
 * The rule used to be `tier !== 'free'`, which is only true of a FIRST
 * purchase. Every upgrade and every repeat starts from a tier that is already
 * not free, so the return screen declared success on its first poll and showed
 * the buyer the plan they had just paid to leave.
 */
export function entitlementChanged(before: Entitlement | null, now: Entitlement): boolean {
  // Nothing recorded: a buyer who reached the return URL without going through
  // redirectToCheckout (a bookmarked URL, storage cleared, a second tab), or a
  // snapshot write that failed. The old rule is the only one available, and it
  // is right for a first purchase.
  if (!before) return now.tier !== 'free';
  return (
    now.tier !== before.tier ||
    now.canCreateTrip !== before.canCreateTrip ||
    now.passesAvailable !== before.passesAvailable
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// The stripe_* names below are the REAL contract, not backend-compat aliases.
// They carried @deprecated tags from the months when the backend proxied to
// Clerk Billing and these were pass-through echoes; billing returned to
// Stripe-direct in JAR-657, so the ids are Stripe's own and every one of them
// is read by live code (JAR-696). Marking a live field deprecated tells the
// next reader to design it out, which is the opposite of true here.
export interface SubscriptionPlan {
  id: string;
  name: string;
  /** Stripe's price id. The plan's lookup key resolves to this server-side;
   *  the amount charged comes from Stripe, never from price_cents below. */
  stripe_price_id: string;
  stripe_product_id?: string;
  /** Stripe's stable handle for the Price — `jt_explore_annual` and friends.
   *  Optional because the server tags it `omitempty` (core models.go:18): a plan
   *  with an empty key serialises no field at all, so this arrives as
   *  `undefined` rather than "". Anything matching on it must check for presence
   *  first, or two undefined values compare equal and select the wrong plan.
   *  This is the identifier marketing links with, since plan `id` is a database
   *  row nothing outside this service should know. */
  stripe_lookup_key?: string;
  description?: string;
  price_cents: number;
  /** The first year's price, in cents, for an Early Access invitee who hasn't
   *  redeemed, on the annual plan. Absent for everyone else. `price_cents`
   *  stays the renewal price. */
  early_access_price_cents?: number;
  currency: string;
  interval_type: 'month' | 'year' | 'one_time';
  product_type: 'subscription' | 'one_time';
  features: Record<string, unknown>;
  is_active: boolean;
  sort_order: number;
}

/** Human-readable price for a plan card — "$99 / year", "$24.99 · one trip".
 *  One source of truth so the Paywall, Plan & billing, and onboarding PlanGate
 *  all label prices identically. */
export function formatPlanPrice(plan: SubscriptionPlan): string {
  // Guard against malformed pricing (NaN / Infinity / negative) so a bad plan
  // shows a calm placeholder instead of "$NaN" / "$∞".
  if (!isPrice(plan.price_cents)) return 'Price unavailable';
  const amount = formatCents(plan.price_cents, plan.currency);
  return plan.interval_type === 'year'
    ? `${amount} / year`
    : plan.interval_type === 'month'
      ? `${amount} / month`
      : `${amount} · one trip`;
}

/** A plan card's price, said one way on every card (PlanPrice renders it). With
 *  an Early Access first-year price (JAR-3179) the regular yearly price is
 *  struck through, then the card leads with the first year's and says what it
 *  renews at: "$99 / year" struck, "$79 your first year", "for a limited time,
 *  then $99 / year". A struck price claims a saving, so a first-year price that
 *  saves nothing is not shown as one. Any other plan leads with formatPlanPrice's
 *  line alone. */
export function planCardPrice(plan: SubscriptionPlan): { regular?: string; lead: string; renewal?: string } {
  const first = earlyAccessCents(plan);
  if (first === null) return { lead: formatPlanPrice(plan) };
  const regular = formatPlanPrice(plan);
  return { regular, lead: `${formatCents(first, plan.currency)} your first year`, renewal: `for a limited time, then ${regular}` };
}

/** The Early Access first-year price a card shows, or null: a yearly plan's
 *  only, a real price only, and only one that saves something on the regular
 *  price. One rule for the price (planCardPrice) and the saving badge
 *  (annualSavingFor), so the badge cannot measure a price the card does not
 *  show. */
function earlyAccessCents(plan: SubscriptionPlan): number | null {
  const first = plan.early_access_price_cents;
  return plan.interval_type === 'year' && isPrice(first) && isPrice(plan.price_cents) && first < plan.price_cents ? first : null;
}

/** A plan card's title. Explore's two cadences are one product with one name,
 *  so the cadence comes from the plan's interval, in the pricing page's words:
 *  "Explore Annual", "Explore Monthly". A Trip Pass is its name alone. */
export function planTitle(plan: SubscriptionPlan): string {
  if (plan.interval_type === 'year') return `${plan.name} Annual`;
  if (plan.interval_type === 'month') return `${plan.name} Monthly`;
  return plan.name;
}

function isPrice(cents: unknown): cents is number {
  return typeof cents === 'number' && Number.isFinite(cents) && cents >= 0;
}

function formatCents(cents: number, currency: string): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** The "Save X%" badge for one plan card, or null when there is no honest claim
 *  to make about it.
 *
 *  This replaces a hardcoded "Best value" badge. A claim like that is only true
 *  relative to something, and stating it without the number invites the reader
 *  to wonder. Derived from the catalogue that is already on screen, so it cannot
 *  drift from the prices next to it — which is exactly how a Resend template
 *  ended up advertising a discontinued plan and a four-cent-wrong Trip Pass.
 *
 *  **One function, per plan, on purpose.** This used to be two exported halves —
 *  a predicate for "should this card show it" and a calculation for "what is the
 *  number" — which every consumer had to remember to `&&` together. Nothing
 *  enforced that pairing, so the predicate could say yes while the calculation
 *  said null (a single-cadence catalogue, or a non-positive annual price), and a
 *  future call site rendering only one of them would print "Save %" or a stale
 *  percentage against the wrong price (review). Two answers that must agree, and
 *  no way to make them, is a bug waiting for its call site; there is now one
 *  answer and nothing to disagree with. */
export function annualSavingFor(
  plan: SubscriptionPlan,
  plans: SubscriptionPlan[],
): number | null {
  // The claim is "the annual plan costs less than twelve monthly payments",
  // which is true of exactly one card. Consumers gated on
  // `product_type === 'subscription'`, which is true of BOTH cadences — so in a
  // both-cadence catalogue the monthly card showed "Save 31%" next to the more
  // expensive price (review M1).
  //
  // is_active is part of the rule, not an extra: the comparison below uses only
  // plans a user can buy, and Paywall and SubscriptionManager render whatever
  // the backend returns without filtering. Without this a de-listed annual plan
  // still rendered "Save X%" — a discount on something no longer on sale,
  // computed from plans it is not part of (review M4).
  if (!plan.is_active || plan.product_type !== 'subscription' || plan.interval_type !== 'year') {
    return null;
  }

  // Only plans a user can actually buy. A de-listed monthly plan used to drive
  // a "Save 31%" claim against a price nobody was shown — the exact
  // advertising-a-discontinued-plan failure this function exists to avoid
  // (review M2).
  const sellable = plans.filter((p) => p.is_active && p.product_type === 'subscription');
  const years = sellable.filter((p) => p.interval_type === 'year');
  const months = sellable.filter((p) => p.interval_type === 'month');

  // Exactly one of each, or no claim. The previous version took the FIRST of
  // each cadence, so a multi-tier catalogue would badge every annual card with
  // a percentage computed from whichever annual and monthly happened to sort
  // first — a Pro annual advertising the Basic saving, next to the Pro price
  // (review L5). Comparing within a tier needs a tier the model does not carry;
  // until it does, ambiguity is answered with silence rather than a guess. The
  // badge disappearing is a missing badge, which is recoverable; the wrong
  // percentage next to a real price is a false claim about money.
  if (years.length !== 1 || months.length !== 1) return null;
  const [year] = years;
  const [month] = months;
  if (year.id !== plan.id) return null;

  // Same money on both sides, or the percentage is arithmetic on two different
  // units. A catalogue can legitimately carry prices in more than one currency,
  // and dividing a USD annual by a EUR monthly produces a number that looks
  // like a discount and is not one — the dishonest claim this whole function
  // exists to avoid making.
  if (year.currency !== month.currency) return null;

  if (!Number.isFinite(year.price_cents) || !Number.isFinite(month.price_cents)) return null;
  if (month.price_cents <= 0) return null;
  // A zero or negative annual price is data corruption, not a discount. Without
  // this, annual(-9900) against monthly(1195) computed "Save 169%" while
  // formatPlanPrice rendered "Price unavailable" on the same card — the badge
  // and the price contradicting each other on screen (review M3).
  if (year.price_cents <= 0) return null;
  // Measured on the price the annual card leads with (JAR-3179, Brent): the
  // Early Access first year when core sends one, else the regular annual price,
  // against a full year at the monthly price either way. $79 against 12 x
  // $11.95 is 45%; $99 is 31%.
  const annualCents = earlyAccessCents(year) ?? year.price_cents;
  const pct = Math.round((1 - annualCents / (month.price_cents * 12)) * 100);
  return pct > 0 ? pct : null;
}

export interface Subscription {
  id: string;
  user_id: string;
  plan_id: string;
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  status: string;
  trip_id?: string;
  consumed_at?: string;
  readonly_from?: string;
  current_period_start?: string;
  current_period_end?: string;
  trial_start?: string;
  trial_end?: string;
  canceled_at?: string;
  ended_at?: string;
  created_at: string;
  updated_at: string;
  plan?: SubscriptionPlan;
}

export interface PaymentTransaction {
  id: string;
  user_id: string;
  subscription_id?: string;
  stripe_payment_intent_id?: string;
  stripe_invoice_id?: string;
  amount_cents: number;
  currency: string;
  status: string;
  failure_message?: string;
  refund_amount_cents: number;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface CheckoutSessionResponse {
  session_id: string;
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface TripPass {
  id: string;
  plan_id: string;
  plan_name: string;
  status: 'active' | 'in_use' | 'consumed';
  trip_id?: string;
  trip_name?: string;
  consumed_at?: string;
  readonly_from?: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Payments API
// ---------------------------------------------------------------------------

/**
 * Where Stripe sends the buyer back. One definition, because these were typed
 * out in the route table, at the checkout call sites and in the defaults below,
 * and a rename in any one of them sends a paid buyer through the catch-all to
 * /trips with no confirmation (web-app#252 review). The App-level route rows
 * pin the public paths; everything else references these.
 */
/**
 * Where Stripe's billing portal sends a customer back to: the Settings tab they
 * left (JAR-1698). A path of its own because Settings is otherwise unaddressable,
 * and one definition because App routes it and SubscriptionManager hands it to
 * Stripe — two copies of a return URL is how one of them ends up wrong.
 */
export const SETTINGS_PATH = '/settings';

export const CHECKOUT_RETURN_PATHS = {
  success: '/checkout/success',
  canceled: '/checkout/canceled',
} as const;

/**
 * Get available subscription plans
 */
export async function listPlans(): Promise<SubscriptionPlan[]> {
  if (isDemoSession()) return DEMO_PLANS;
  const result = await api.get<{ plans: SubscriptionPlan[] }>("/payments/plans");

  if (result.error) {
    // Backend unreachable — show sample plans so the plan gate renders in dev
    // without a live backend (JAR-293).
    if (getBackendStatus() === 'offline') return DEMO_PLANS;
    throw new Error(result.error.message || "Failed to fetch plans");
  }

  return result.data?.plans || [];
}

/**
 * The current user's entitlement — drives the create-trip gate. Backend computes
 * this from Stripe subscription state. Demo models a Trip Pass user whose
 * pass is spent (so creating another trip hits the paywall,
 * while their journals stay open).
 */
export async function getEntitlement(): Promise<Entitlement> {
  if (isDemoSession()) return { tier: 'trip_pass', canCreateTrip: false, journalAccess: true, passesAvailable: 0 };
  try {
    const [sub, passes] = await Promise.all([
      getSubscription().catch(() => null),
      listAvailablePasses().catch(() => [] as TripPass[]),
    ]);
    // Core's rule for a live Explore subscription (payments.LiveSubscription):
    // a trial creates trips the same as a paid subscription (JAR-1761).
    const live = sub?.status === 'active' || sub?.status === 'trialing';
    const subscriber = live && sub?.plan?.product_type === 'subscription';
    const tier: Entitlement['tier'] = subscriber ? 'subscriber' : (sub || passes.length) ? 'trip_pass' : 'free';
    return { tier, canCreateTrip: subscriber || passes.length > 0, journalAccess: true, passesAvailable: passes.length };
  } catch {
    // Fail closed — never let a backend outage silently drop the paywall.
    return { tier: 'free', canCreateTrip: false, journalAccess: true, passesAvailable: 0 };
  }
}

/**
 * Get current user's subscription (Stripe, via the backend).
 */
export async function getSubscription(): Promise<Subscription> {
  const result = await api.get<Subscription>("/payments/subscription");

  if (result.error) {
    throw new Error(result.error.message || "Failed to fetch subscription");
  }

  if (!result.data) {
    throw new Error("No subscription found");
  }

  return result.data;
}

/**
 * Get subscription history
 */
export async function getSubscriptionHistory(): Promise<Subscription[]> {
  const result = await api.get<{ subscriptions: Subscription[] }>("/payments/subscription/history");

  if (result.error) {
    throw new Error(result.error.message || "Failed to fetch subscription history");
  }

  return result.data?.subscriptions || [];
}

/**
 * Create a billing checkout session for subscription purchase or Trip Pass.
 * The backend calls Stripe.
 */
export async function createCheckoutSession(planId: string, successUrl: string, cancelUrl: string): Promise<CheckoutSessionResponse> {
  // Demo has no billing backend — return an empty url so redirectToCheckout no-ops
  // and the paywall shows its demo note instead of navigating away.
  if (isDemoSession()) return { session_id: 'demo', url: '' };
  const result = await api.post<CheckoutSessionResponse>("/payments/create-checkout-session", {
    plan_id: planId,
    success_url: successUrl,
    cancel_url: cancelUrl,
  });

  if (result.error) {
    throw new Error(result.error.message || "Failed to create checkout session");
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

/**
 * Create a billing portal session for managing subscriptions.
 * The backend calls Stripe.
 */
export async function createPortalSession(returnUrl: string): Promise<PortalSessionResponse> {
  // Demo has no billing portal — empty url so the UI shows a note.
  if (isDemoSession()) return { url: '' };
  const result = await api.post<PortalSessionResponse>("/payments/create-portal-session", {
    return_url: returnUrl,
  });

  if (result.error) {
    throw new Error(result.error.message || "Failed to create portal session");
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

/**
 * Get payment transaction history
 */
export async function listTransactions(limit: number = 20, offset: number = 0): Promise<PaymentTransaction[]> {
  const result = await api.get<{ transactions: PaymentTransaction[] }>(
    `/payments/transactions?limit=${limit}&offset=${offset}`
  );

  if (result.error) {
    throw new Error(result.error.message || "Failed to fetch transactions");
  }

  return result.data?.transactions || [];
}

/**
 * Get all available (unconsumed) Trip Passes for the current user
 */
export async function listAvailablePasses(): Promise<TripPass[]> {
  // Demo models a Trip Pass user whose pass is spent — no available passes.
  if (isDemoSession()) return [];
  const result = await api.get<{ passes: TripPass[] }>("/payments/passes");

  if (result.error) {
    throw new Error(result.error.message || "Failed to fetch passes");
  }

  return result.data?.passes || [];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Redirect to billing checkout. Returns false if the user is on a free plan
 * and no redirect is needed. The backend calls Stripe.
 */
export async function redirectToCheckout(
  planId: string,
  options?: { successPath?: string; cancelPath?: string }
): Promise<boolean> {
  const base = window.location.origin;
  const successUrl = `${base}${options?.successPath || CHECKOUT_RETURN_PATHS.success}`;
  const cancelUrl = `${base}${options?.cancelPath || CHECKOUT_RETURN_PATHS.canceled}`;

  const result = await createCheckoutSession(planId, successUrl, cancelUrl);

  if (result.url) {
    // Record what they hold BEFORE leaving, so the return screen can tell a
    // purchase that landed from one that has not. Every checkout in the app
    // goes through this function -- Paywall, PlanGate and SubscriptionManager
    // -- so this is the one place it has to happen.
    //
    // After the redirect is decided and before the navigation, deliberately: a
    // checkout that never starts must not leave a snapshot behind for the next
    // one to read.
    await rememberEntitlementBeforeCheckout();
    window.location.href = result.url;
    return true;
  }

  return false;
}

const PRE_CHECKOUT_KEY = 'jarvis_pre_checkout_entitlement';

/**
 * Snapshot the current entitlement, for `entitlementChanged` to compare against.
 *
 * The previous checkout's snapshot is removed BEFORE the new one is written. A
 * snapshot left over from an earlier checkout would make this purchase look
 * like it had already landed, so a failed write has to leave no snapshot at all.
 */
export async function rememberEntitlementBeforeCheckout(): Promise<void> {
  try {
    sessionStorage.removeItem(PRE_CHECKOUT_KEY);
    sessionStorage.setItem(PRE_CHECKOUT_KEY, JSON.stringify(await getEntitlement()));
  } catch {
    // Storage unavailable, or the write failed. The return screen falls back
    // to the first-purchase rule, which is worse but not broken.
  }
}

/**
 * The entitlement recorded before this checkout, or null.
 *
 * Reading does not remove it: a refresh of the return screen, or a StrictMode
 * double render, reads it again. It lasts until the purchase is seen to land
 * (`forgetEntitlementBeforeCheckout`) or the next checkout replaces it, and
 * sessionStorage ends it with the tab.
 */
export function readEntitlementBeforeCheckout(): Entitlement | null {
  try {
    const raw = sessionStorage.getItem(PRE_CHECKOUT_KEY);
    return raw ? (JSON.parse(raw) as Entitlement) : null;
  } catch {
    return null;
  }
}

/** The purchase has landed, so there is nothing left to compare against. */
export function forgetEntitlementBeforeCheckout(): void {
  try {
    sessionStorage.removeItem(PRE_CHECKOUT_KEY);
  } catch {
    // Storage unavailable: nothing was stored either.
  }
}

/**
 * Check if a plan is a one-time purchase (Trip Pass) vs a subscription
 */
export function isOneTimePlan(plan: SubscriptionPlan): boolean {
  return plan.product_type === 'one_time';
}

/**
 * Get the display label for a Trip Pass status
 */
export function getPassStatusLabel(status: TripPass['status']): string {
  const labels: Record<TripPass['status'], string> = {
    active: 'Available',
    in_use: 'In Use',
    consumed: 'Completed',
  };
  return labels[status] || status;
}
